import axios from 'axios'
import {defineStore} from 'pinia'
import { notifyNativeAppOfAddToCart, notifyNativeAppOfCustomsChargeLine } from '../helpers/venn-apps'

export const useCartStore = defineStore('CartStore', {
  state: () => {
    return {
      cart: {
        loading: true,
        items: []
      },
      cartIsUpdating: false,
      slideoutCartIsOpen: false,
      // Prevents re-entrant syncCustomsCharge when the sync itself triggers cartUpdated
      _customsChargeSyncing: false
    }
  },
  actions: {
    async getCart() {
      document.addEventListener('cartRefresh', event => {
        if (event.detail.token) {
          this.cart = event.detail
          this.cartIsUpdating = false
          this.syncCustomsCharge()
        }
      })
      document.addEventListener('cartItemAdded', () => {
        this.syncCustomsCharge()
      })
      document.addEventListener('cartUpdated', () => {
        if (!this._customsChargeSyncing) {
          this.syncCustomsCharge()
        }
      })
      await axios.get(window.Shopify.routes.root + 'cart.js').then(async response => {
        if (this.cartIsUpdating) return
        this.cart = response.data
        this.cartIsUpdating = false
        await this.syncCustomsCharge()
      })
    },
    async addItems(payload) {
      this.cartIsUpdating = true
      await axios.post(window.Shopify.routes.root + 'cart/add.js', payload)
        .then(async addResponse => {
          this.cartIsUpdating = false
          document.dispatchEvent(new Event('cartItemAdded'))
          console.log(
            `items added to your cart:`,
            addResponse.data.items
          )
          notifyNativeAppOfAddToCart(payload.items)
        })
        .catch((e) => {
          alert(e.response.data.description)
          console.error(e)
          this.cartIsUpdating = false
        })
    },
    async changeItems(payload) {
      await this.processChange(payload, 'change')
    },
    async clearCart() {
      await this.processChange(false, 'clear')
    },
    async updateItems(payload) {
      await this.processChange(payload, 'update')
    },
    async removeItem(key) {
      await this.changeItems({
        id: key,
        quantity: 0
      })
    },
    async processChange(payload, endpoint) {
      this.cartIsUpdating = true
      await axios.post(`${window.Shopify.routes.root}cart/${endpoint}.js`, payload)
        .then(async response => {
          this.cart = response.data
          this.cartIsUpdating = false
          document.dispatchEvent(new Event('cartUpdated'))
          await this.syncCustomsCharge()
        })
        .catch((e) => {
          alert(e.response.data.message)
          console.log(e)
          this.cartIsUpdating = false
        })
    },

    openSlideoutCart() {
      this.slideoutCartIsOpen = true
      document.body.classList.add('overflow-hidden')
    },

    closeSlideoutCart(event) {
      if (event.target.hasAttribute('data-close')) {
        this.slideoutCartIsOpen = false
        document.body.classList.remove('overflow-hidden')
      }
    },

    /**
     * SHARED COUNTING RULE — v1 FROZEN 2026-06-30
     * Both this function and the CartCheckoutValidation Function must produce the same
     * quantity. Do not diverge without updating both layers and re-running the
     * theme-count == function-count matrix (bundle-handling.md §6).
     *
     * expectedQty = |Set of distinct product_type values from chargeable lines|
     *
     * CHARGEABLE LINE contributions:
     *   Normal line → item.product_type from /cart.js
     *   Metafields bundle parent (product_type === 'DUMMY' OR properties._bundle_parent):
     *     → parse properties._bundle_options (JSON string array of selected variant IDs)
     *     → map each variantId → productType via window.theme.bundleVariantTypes
     *     → skip the entire parent if final_price === 0 or has free-gift discount title
     *     → DUMMY parent type itself is never added — only mapped component types are
     *   Legacy bundle child (properties.bundle) → item.product_type if final_price > 0
     *
     * EXCLUDED entirely (in order of check):
     *   1. Customs charge line itself — matched by variant_id OR product_id from settings
     *      (never by product_type 'Other' — charge product type may change)
     *   2. Free items (D13) — item.final_price === 0 (price-based, parity with the bundle-free
     *      check below and the CartCheckoutValidation Function's cost.totalAmount check), OR
     *      discount title === window.theme.cart.freeGift.discountScriptTitle as a secondary
     *      signal. Price is the primary check: it catches any free line regardless of which
     *      discount mechanism zeroed it, so a free-gift discount rename/mismatch can't cause
     *      a free item to be miscounted as a chargeable type.
     *   3. Blank / empty product_type
     *   4. DUMMY parent line type (component types from _bundle_options mapping only)
     *   5. Lines with properties.bundle are handled in the legacy-bundle-child branch
     *
     * DE-DUPE: same product_type from a loose item + a bundle component → counted once (Set)
     *
     * Known limitation (re-audit after D13/D14): per-component free exclusion within a paid
     * bundle (e.g. the tee in eats-and-free-tee-bundle) is not distinguishable from the
     * cart line alone. The paid-bundle guard (final_price === 0) covers free bundles in full;
     * individual free slots inside a paid bundle are not yet excluded. Requires additional
     * per-slot metadata or a variant allowlist; escalate if function count diverges.
     *
     * @param {Array} cartItems - Full cart.items array (not the filtered items getter)
     * @returns {number} Expected customs charge line quantity
     */
    isBundleParentLine(item) {
      return item.product_type === 'DUMMY' ||
        !!(item.properties && item.properties._bundle_parent)
    },

    /**
     * Distinct bundle component product_type values for a metafields-mode bundle parent line.
     * Returns a sorted array (stable for comparison and JSON serialization). Empty when the
     * bundle is free, not a bundle parent, or has no mappable component types.
     * Also written to line property _customs_bundle_types for the validation function (S2).
     *
     * @param {Object} item - Cart line from /cart.js
     * @returns {string[]} Sorted distinct product_type values
     */
    computeBundleCustomsTypes(item) {
      if (!this.isBundleParentLine(item)) return []

      const freeGiftTitle = window.theme?.cart?.freeGift?.discountScriptTitle
      const bundleVariantTypes = window.theme?.bundleVariantTypes || {}

      const bundleFree = item.final_price === 0 ||
        !!(freeGiftTitle && item.discounts?.find(d => d.title === freeGiftTitle))
      if (bundleFree) return []

      let bundleOptionIds = []
      try {
        const rawOptions = item.properties?._bundle_options
        if (rawOptions) {
          bundleOptionIds = typeof rawOptions === 'string'
            ? JSON.parse(rawOptions)
            : Array.isArray(rawOptions) ? rawOptions : []
        }
      } catch {
        return []
      }

      const productTypes = new Set()
      for (const variantId of bundleOptionIds) {
        const productType = bundleVariantTypes[variantId] ?? bundleVariantTypes[String(variantId)]
        if (productType && productType !== '' && productType !== 'DUMMY') {
          productTypes.add(productType)
        }
      }
      return [...productTypes].sort()
    },

    parseCustomsBundleTypesProperty(value) {
      if (!value) return []
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value
        return Array.isArray(parsed) ? [...parsed].sort() : []
      } catch {
        return []
      }
    },

    computeExpectedCustomsChargeQuantity(cartItems) {
      const chargeSettings = window.theme?.cart?.customsCharge
      const freeGiftTitle = window.theme?.cart?.freeGift?.discountScriptTitle

      const chargeVariantId = chargeSettings?.variantId
      const chargeProductId = chargeSettings?.productId

      const productTypes = new Set()

      for (const item of cartItems) {
        // Exclusion 1: customs charge line itself (match by ID, never by type)
        if (
          (chargeVariantId && item.variant_id === chargeVariantId) ||
          (chargeProductId && item.product_id === chargeProductId)
        ) {
          continue
        }

        // Exclusion 2: free items (D13) — price-based, with discount-title as a secondary signal
        const isFreeItem = item.final_price === 0 ||
          !!(freeGiftTitle && item.discounts?.find(d => d.title === freeGiftTitle))
        if (isFreeItem) {
          continue
        }

        if (this.isBundleParentLine(item)) {
          for (const productType of this.computeBundleCustomsTypes(item)) {
            productTypes.add(productType)
          }
          continue // DUMMY parent type itself is never added to the set
        }

        // Legacy bundle children (properties.bundle): count product_type if not free
        // These lines are excluded from the items getter display but are counted here
        if (item.properties?.bundle) {
          if (item.product_type && item.final_price > 0) {
            productTypes.add(item.product_type)
          }
          continue
        }

        // Exclusion 3: blank product_type on normal lines
        if (!item.product_type) continue

        // Normal line: add product_type to the de-duplication set
        productTypes.add(item.product_type)
      }

      return productTypes.size
    },

    /**
     * Idempotent reconcile: ensures the customs charge cart line quantity equals
     * computeExpectedCustomsChargeQuantity(). Called after every cart mutation and on
     * initial load. Safe to call redundantly — guards prevent double-execution.
     *
     * Flow (enabled path):
     *   1. Bail if cartIsUpdating (another mutation in flight)
     *   2. Bail if _customsChargeSyncing (this sync already running — avoids cartUpdated loop)
     *   3. Compute expectedQty from full cart.items
     *   4. Find existing charge line by variant/product ID
     *   5. add / change / noop to reach expected quantity
     *   6. Write _customs_bundle_types on bundle parent lines (validation function primary source)
     *   7. Always update this.cart from server response after any mutation
     */
    async syncBundleCustomsTypes() {
      let updated = true
      while (updated) {
        updated = false
        for (const item of this.cart?.items || []) {
          if (!this.isBundleParentLine(item)) continue

          const expectedTypes = this.computeBundleCustomsTypes(item)
          const currentTypes = this.parseCustomsBundleTypesProperty(
            item.properties?._customs_bundle_types
          )

          if (expectedTypes.length === 0) {
            if (currentTypes.length === 0) continue
            const properties = { ...item.properties, _customs_bundle_types: '' }
            const changeResponse = await axios.post(window.Shopify.routes.root + 'cart/change.js', {
              id: item.key,
              quantity: item.quantity,
              properties
            })
            this.cart = changeResponse.data
            updated = true
            break
          }

          const expectedJson = JSON.stringify(expectedTypes)
          if (JSON.stringify(currentTypes) === expectedJson) continue

          const properties = {
            ...item.properties,
            _customs_bundle_types: expectedJson
          }

          const changeResponse = await axios.post(window.Shopify.routes.root + 'cart/change.js', {
            id: item.key,
            quantity: item.quantity,
            properties
          })
          this.cart = changeResponse.data
          updated = true
          break
        }
      }
    },

    async syncCustomsCharge() {
      const chargeSettings = window.theme?.cart?.customsCharge

      // Feature disabled: remove charge line if it somehow ended up in the cart
      if (!this.customsChargeEnabled) {
        const chargeVariantId = chargeSettings?.variantId
        const chargeProductId = chargeSettings?.productId
        const existingLine = this.cart?.items?.find(item =>
          (chargeVariantId && item.variant_id === chargeVariantId) ||
          (chargeProductId && item.product_id === chargeProductId)
        )
        if (existingLine) {
          this._customsChargeSyncing = true
          this.cartIsUpdating = true
          await axios.post(window.Shopify.routes.root + 'cart/change.js', {
            id: existingLine.key,
            quantity: 0
          })
            .then(response => { this.cart = response.data })
            .catch(e => console.error('[customs-charge] remove on disabled:', e))
            .finally(() => {
              this._customsChargeSyncing = false
              this.cartIsUpdating = false
            })
        }
        return
      }

      // Guard: another cart mutation is in flight — skip to avoid race conditions
      if (this.cartIsUpdating) return

      // Guard: this sync is already running — prevents cartUpdated listener from looping
      if (this._customsChargeSyncing) return

      this._customsChargeSyncing = true
      this.cartIsUpdating = true

      try {
        const { variantId, productId } = chargeSettings

        // Must use full cart.items, not the filtered items getter (charge line excluded there)
        const expectedQty = this.computeExpectedCustomsChargeQuantity(this.cart.items)

        // Find charge line by ID — never by product_type (charge type may change)
        const existingLine = this.cart.items.find(item =>
          (variantId && item.variant_id === variantId) ||
          (productId && item.product_id === productId)
        )

        if (expectedQty === 0 && !existingLine) {
          // Nothing to do
          return
        }

        if (expectedQty > 0 && !existingLine) {
          // cart/add.js response is a partial add result — fetch full cart afterwards
          await axios.post(window.Shopify.routes.root + 'cart/add.js', {
            items: [{ id: variantId, quantity: expectedQty }]
          })
          const cartResponse = await axios.get(window.Shopify.routes.root + 'cart.js')
          this.cart = cartResponse.data
          notifyNativeAppOfCustomsChargeLine({
            variantId,
            productId: chargeSettings.productId,
            quantity: expectedQty
          })
        } else if (existingLine && existingLine.quantity !== expectedQty) {
          // qty 0 removes the line; change.js returns full updated cart
          const changeResponse = await axios.post(window.Shopify.routes.root + 'cart/change.js', {
            id: existingLine.key,
            quantity: expectedQty
          })
          this.cart = changeResponse.data
          if (expectedQty > 0) {
            notifyNativeAppOfCustomsChargeLine({
              variantId,
              productId: chargeSettings.productId,
              quantity: expectedQty
            })
          }
        } else if (existingLine && existingLine.quantity === expectedQty && expectedQty > 0) {
          notifyNativeAppOfCustomsChargeLine({
            variantId,
            productId: chargeSettings.productId,
            quantity: expectedQty
          })
        }

        await this.syncBundleCustomsTypes()

      } catch (e) {
        console.error('[customs-charge] syncCustomsCharge error:', e)
      } finally {
        this._customsChargeSyncing = false
        this.cartIsUpdating = false
      }
    }
  },

  getters: {
    /**
     * True only when both the feature flag and a charge product are configured.
     * Gates all FE-S2 behaviour — never gate on a domain string (implementation-scope.md D10).
     */
    customsChargeEnabled() {
      return !!(window.theme?.cart?.customsCharge?.enabled && window.theme?.cart?.customsCharge?.variantId)
    },

    /**
     * Returns the customs charge cart line, or null if not present.
     * Exposed for display components (Step 3b) to render the locked line.
     */
    customsChargeItem() {
      if (!this.cart || !this.customsChargeEnabled) return null
      const { variantId, productId } = window.theme.cart.customsCharge
      return this.cart.items.find(item =>
        (variantId && item.variant_id === variantId) ||
        (productId && item.product_id === productId)
      ) ?? null
    },

    customsChargeLinePrice() {
      return this.customsChargeItem?.final_line_price ?? 0
    },

    subtotalExcludingCustoms() {
      return this.totalPrice - this.customsChargeLinePrice
    },

    items() {
      if (this.cart) {
        return this.cart.items.filter(item => {
          const isFreeGift = item.discounts.find(discount => discount.title === window.theme.cart.freeGift.discountScriptTitle)
          const isLegacyBundleChild = item.properties && item.properties.bundle
          const isCustomsCharge = this.customsChargeEnabled && (
            (window.theme.cart.customsCharge.variantId && item.variant_id === window.theme.cart.customsCharge.variantId) ||
            (window.theme.cart.customsCharge.productId && item.product_id === window.theme.cart.customsCharge.productId)
          )
          return !(isFreeGift || isLegacyBundleChild || isCustomsCharge)
        })
      } else {
        return []
      }
    },
    bundleItems() {
      if (this.cart) {
        return this.cart.items.filter(item => {
          return item.properties && item.properties.bundle
        })
      } else {
        return []
      }
    },
    itemCount() {
      let amount = 0
      if (this.cart) {
        this.items.forEach(item => {
          amount += item.quantity
        })
      }
      return amount
    },
    totalPrice() {
      return this.cart ? this.cart.total_price : 0
    },
    cartIsEmpty() {
      return this.itemCount === 0
    },
    cartContainsFreeGift() {
      return !!this.cart.items.find(item => {
        return !!item.discounts.find(discount => discount.title === window.theme.cart.freeGift.discountScriptTitle)
      })
    },
    freeGiftItem() {
      if (this.cartContainsFreeGift) {
        return this.cart.items.find(item => {
          return !!item.discounts.find(discount => discount.title === window.theme.cart.freeGift.discountScriptTitle)
        })
      }
      return null
    },
    freeGiftDeliveryExceptionProducts() {
      return [...window.theme.cart.freeGift.deliveryExceptionProducts]
    },
    cartContainsNoneExcemptItems() {
      let noneExcemptProductFound = false
      this.items.forEach(item => {
        if (!this.freeGiftDeliveryExceptionProducts.find(product => product.handle === item.handle)) {
          noneExcemptProductFound = true
          return
        }
      })
      return noneExcemptProductFound
    }
  },
})
